import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { MiscContentDetailsPageCombinedState, MiscContentDetailsPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { MiscContentDetailsPageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<MiscContentDetailsPageCombinedState> };

type Props = {
	countryCode: string;
	slug: string;
	prefetchedData: MiscContentDetailsPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const MiscContentDetailsPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	countryCode,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(countryCode, slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	const { details } = pageData.data;

	return (
		<MiscContentDetailsPageDataContextProvider pageData={pageData}>
			<TrackingContext
				properties={{
					date: details.createdAt,
					post_id: `post_${details.id}`,
					slug: details.slug,
				}}
				defaultOverrides={{ country_code: countryCode }}
			>
				{children}
			</TrackingContext>
		</MiscContentDetailsPageDataContextProvider>
	);
};
